import { ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { filterBoolean, trackByProp } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import { JobsCombinedSelectors, JobsIdMap } from '@recruitee/offers-data';
import type { OfferBrief } from '@recruitee/offers-types';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  OfferStatusIndicatorComponent,
  TabComponent,
  TabsComponent,
} from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

type DialogData = {
  offers: OfferBrief[];
};

@Component({
  selector: 'rt-assigned-jobs-dialog',
  templateUrl: './assigned-jobs-dialog.component.html',
  styleUrls: ['./assigned-jobs-dialog.component.less'],
  imports: [
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    OfferStatusIndicatorComponent,
    RouterModule,
    TabComponent,
    TabsComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class AssignedJobsDialogComponent implements DialogComponentI<DialogData, void> {
  public searchControl = new FormControl<string>('', { nonNullable: true });

  public visibleOffers: OfferBrief[];
  public offers: OfferBrief[];
  public jobsMap: JobsIdMap;
  public trackById = trackByProp<{ id: string | number }>({ prop: 'id' });

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    protected cdRef: ChangeDetectorRef,
    private store: Store,
    public dialog: DialogRef<AssignedJobsDialogComponent>,
  ) {}

  public setDialogData(data: DialogData, triggerUpdate: boolean) {
    this.visibleOffers = data.offers || [];
    this.offers = data.offers || [];
  }

  public hasAccessToJob(id: number): boolean {
    return !!this.jobsMap?.[id];
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges.subscribe(query => {
      this.visibleOffers = this.offers.filter(offer => offer.title.includes(query));
      this.cdRef.detectChanges();
    });

    this.store
      .select(JobsCombinedSelectors.activeItemsAndArchivedBriefIdMap)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(jobsMap => {
        this.jobsMap = jobsMap;
        this.cdRef.detectChanges();
      });
  }

  public distinctKeyEvent(event: MouseEvent): void {
    if (!event.metaKey) this.close();
  }

  public close(): void {
    this.dialog.close();
  }

  public reset(): void {
    this.searchControl.reset('');
  }
}
